import { Button, MenuTrigger, Progress } from '@eleveo/components-react';
import { ExportIcon } from '@eleveo/icons-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { REPORTS_EXPORT_TYPES, REPORTS_GRANULARITY_TYPES } from './config';
import { EXPORT_CALCULATED_DATA_BUTTON_TAG, EXPORT_MENU_BUTTON_TAG, EXPORT_RAW_DATA_BUTTON_TAG } from './constants';
import { createAuthHeaders, useAuth } from '../../app/AuthorizationProvider';
import { useConfig } from '../../app/ConfigurationProvider';
import { ReportsKpiType, Values } from '../../types';
import { isUnauthorized } from '../../utils/customFetch';
import { downloadBlobFile } from '../../utils/exportSchedule';
import { generateSearchParams } from '../../utils/url';

export interface ReportsAdherenceExportProps {
  agentIds?: string;
  from: string;
  isExportDisabled: boolean;
  reportsViewGranularity: Values<typeof REPORTS_GRANULARITY_TYPES>;
  teamIds?: string;
  to: string;
  visibleKpi?: Values<typeof ReportsKpiType>[];
}

export const ReportsAdherenceExport = ({
  agentIds,
  from,
  isExportDisabled,
  reportsViewGranularity,
  teamIds,
  to,
  visibleKpi,
}: ReportsAdherenceExportProps) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { tenant } = useConfig();

  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const onClickHandler = (type: Values<typeof REPORTS_EXPORT_TYPES>) => async () => {
    setDownloadInProgress(true);

    try {
      if (auth.isTokenInvalid()) {
        console.debug('Token was evaluated as invalid, needs to be re-fetched before request will be executed.');
        await auth.forceUpdateToken();
      }

      // As current BE implementation accepts only one KPI value, when both available KPI values are checked, parameter must be omited
      const wfmKpis =
        type === REPORTS_EXPORT_TYPES.statistics && visibleKpi?.length === 1 ? visibleKpi[0].toUpperCase() : undefined;

      const queryParams = generateSearchParams({ agentIds, from, to, teamIds, wfmKpis });
      const queryParamsSuffix = queryParams ? `?${queryParams}` : '';

      const url = `${window.location.origin}/_rest/wfo-analytics-core/wfm/export/${reportsViewGranularity}/${type}${queryParamsSuffix}`;
      const requestBody = {
        method: 'GET',
        headers: {
          contentType: 'text/csv;charset=UTF-8',
          ...createAuthHeaders(auth.keycloak.token!, tenant),
        },
      };

      const response = await fetch(url, requestBody);

      if (response.ok) {
        await downloadBlobFile(response, t('reports.report', { count: 2 }));

        toast.success(t(`response.success.reports.export.adherence`));
      } else if (response.status >= 500) {
        toast.error(t(`response.unavailable`));
      } else {
        if (isUnauthorized(response.status)) {
          const refreshTokenSucceed = await auth.forceUpdateToken();
          if (refreshTokenSucceed) {
            console.debug('Token was successfully renewed after 401, trying to re-download file.');

            onClickHandler(type)();
          }

          return;
        }

        const message = await response.json();
        toast.warning(t(`response.${message.errorCode}`));
      }
    } finally {
      setDownloadInProgress(false);
    }
  };

  return (
    <>
      {downloadInProgress && <Progress withOverlay />}

      <MenuTrigger
        items={[
          {
            id: '1',
            text: t('reports.rawData'),
            onClick: onClickHandler(REPORTS_EXPORT_TYPES.raw),
            testId: EXPORT_RAW_DATA_BUTTON_TAG,
          },
          {
            id: '2',
            text: t('reports.calculatedData'),
            onClick: onClickHandler(REPORTS_EXPORT_TYPES.statistics),
            testId: EXPORT_CALCULATED_DATA_BUTTON_TAG,
          },
        ]}
      >
        <Button
          aria-label={t('reports.exportData')}
          variant="outline"
          testId={EXPORT_MENU_BUTTON_TAG}
          isDisabled={isExportDisabled}
        >
          <ExportIcon />
        </Button>
      </MenuTrigger>
    </>
  );
};
